import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import {
  Badge,
  Card,
  Empty,
  ErrorState,
  Loading,
  PageIntro,
  formatNumber,
} from "../components/ui.jsx";
import { IconBolt, IconChart } from "../components/icons.jsx";

const RANGES = [
  { key: "24h", label: "Last 24 hours", hours: 24, granularity: "hour" },
  { key: "7d", label: "Last 7 days", hours: 24 * 7, granularity: "hour" },
  { key: "30d", label: "Last 30 days", hours: 24 * 30, granularity: "day" },
  { key: "90d", label: "Last 90 days", hours: 24 * 90, granularity: "day" },
];

export default function Usage() {
  const [rangeKey, setRangeKey] = useState("7d");
  const [deviceNumber, setDeviceNumber] = useState("");

  const range = RANGES.find((r) => r.key === rangeKey);
  const to = new Date();
  const from = new Date(to.getTime() - range.hours * 3600_000);
  const window = {
    from: from.toISOString(),
    to: to.toISOString(),
    granularity: range.granularity,
  };

  const devices = useAsync(() => api.listDevices({ limit: 500 }), []);
  const series = useAsync(
    () =>
      deviceNumber
        ? api.deviceUsage(deviceNumber, window)
        : api.tenantUsage(window),
    [deviceNumber, rangeKey]
  );
  const top = useAsync(
    () => api.topConsumers({ from: window.from, to: window.to, limit: 10 }),
    [rangeKey]
  );

  const buckets = series.data?.buckets ?? [];
  const resolved = series.data?.granularity;
  const total = buckets.reduce((sum, b) => sum + (b.active_energy ?? 0), 0);
  const peak = buckets.reduce((max, b) => Math.max(max, b.max_active_power ?? 0), 0);

  return (
    <>
      <PageIntro title="Consumption analytics">
        Figures come from an hourly rollup maintained as readings arrive, so a wide window
        is as cheap as a narrow one. Energy is the difference between the device's own
        cumulative registers and is never recomputed from power.
      </PageIntro>

      <div className="toolbar">
        <select
          className="select"
          value={deviceNumber}
          onChange={(e) => setDeviceNumber(e.target.value)}
        >
          <option value="">Whole estate</option>
          {(devices.data ?? [])
            .filter((d) => d.Kind !== "NOVALINK")
            .map((d) => (
              <option key={d.Number} value={d.Number}>
                {d.Number}
              </option>
            ))}
        </select>

        <select className="select" value={rangeKey} onChange={(e) => setRangeKey(e.target.value)}>
          {RANGES.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </select>

        <span className="spacer" />
        {resolved && (
          <Badge kind="brand" plain>
            Resolution: {resolved === "day" ? "daily" : resolved === "hour" ? "hourly" : "raw readings"}
          </Badge>
        )}
      </div>

      <div className="grid grid-3" style={{ marginBottom: 20 }}>
        <Card>
          <div className="stat-label">
            <IconBolt /> Total energy
          </div>
          <div className="stat-value">{formatNumber(total, 1)} kWh</div>
          <div className="stat-sub">{range.label.toLowerCase()}</div>
        </Card>
        <Card>
          <div className="stat-label">
            <IconChart /> Peak power
          </div>
          <div className="stat-value">{formatNumber(peak, 0)} W</div>
          <div className="stat-sub">Highest instantaneous reading</div>
        </Card>
        <Card>
          <div className="stat-label">
            <IconChart /> Buckets
          </div>
          <div className="stat-value">{buckets.length}</div>
          <div className="stat-sub">Points in the series</div>
        </Card>
      </div>

      <Card
        title={deviceNumber ? `Consumption for ${deviceNumber}` : "Consumption across the estate"}
        hint="Energy per bucket, with average power overlaid"
      >
        {series.loading ? (
          <Loading />
        ) : series.error ? (
          <ErrorState error={series.error} onRetry={series.reload} />
        ) : buckets.length === 0 ? (
          <Empty title="No data in this window">
            Nothing was reported over this period. Try a wider range, or check that the
            devices are reporting.
          </Empty>
        ) : (
          <UsageChart buckets={buckets} />
        )}
      </Card>

      <div style={{ marginTop: 20 }}>
        <Card title="Highest consumers" hint="Ranked by energy over the selected window">
          {top.loading ? (
            <Loading />
          ) : (top.data ?? []).length === 0 ? (
            <Empty title="Nothing to rank">
              No consumption was recorded in this window.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ border: "none" }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Device</th>
                    <th>Channel</th>
                    <th>Energy</th>
                    <th>Samples</th>
                  </tr>
                </thead>
                <tbody>
                  {top.data.map((row, i) => (
                    <tr key={`${row.device_number}-${row.channel_index}`}>
                      <td>{i + 1}</td>
                      <td>
                        <Link className="mono" to={`/devices/${row.device_number}`}>
                          {row.device_number}
                        </Link>
                      </td>
                      <td>{row.channel_index || "Whole unit"}</td>
                      <td>
                        <strong>{formatNumber(row.active_energy, 1)} kWh</strong>
                      </td>
                      <td className="hint">{row.samples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

/**
 * A dependency-free bar and line chart.
 *
 * A charting library would be several hundred kilobytes for one view, and
 * plain SVG is enough for a bounded series that the server has already
 * aggregated.
 */
function UsageChart({ buckets }) {
  const width = 900;
  const height = 220;
  const padding = { top: 12, right: 44, bottom: 26, left: 46 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const maxEnergy = Math.max(...buckets.map((b) => b.active_energy ?? 0), 0.001);
  const maxPower = Math.max(...buckets.map((b) => b.avg_active_power ?? 0), 0.001);
  const barWidth = Math.max(1, (innerW / buckets.length) * 0.68);

  const x = (i) => padding.left + (innerW / buckets.length) * (i + 0.5);
  const yEnergy = (v) => padding.top + innerH - (v / maxEnergy) * innerH;
  const yPower = (v) => padding.top + innerH - (v / maxPower) * innerH;

  const linePath = buckets
    .map((b, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${yPower(b.avg_active_power ?? 0).toFixed(1)}`)
    .join(" ");

  const labelEvery = Math.max(1, Math.ceil(buckets.length / 8));

  return (
    <>
      <svg className="chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img">
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => (
          <g key={frac}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={padding.top + innerH * frac}
              y2={padding.top + innerH * frac}
              stroke="var(--border)"
              strokeWidth="1"
            />
            <text
              x={padding.left - 8}
              y={padding.top + innerH * frac + 4}
              textAnchor="end"
              fontSize="10"
              fill="var(--text-subtle)"
            >
              {formatNumber(maxEnergy * (1 - frac), 1)}
            </text>
          </g>
        ))}

        {buckets.map((b, i) => {
          const value = b.active_energy ?? 0;
          const y = yEnergy(value);
          return (
            <rect
              key={b.start}
              x={x(i) - barWidth / 2}
              y={y}
              width={barWidth}
              height={Math.max(0, padding.top + innerH - y)}
              fill="var(--brand-indigo)"
              opacity="0.82"
              rx="2"
            >
              <title>
                {new Date(b.start).toLocaleString()}: {formatNumber(value, 2)} kWh,{" "}
                {b.samples} samples
              </title>
            </rect>
          );
        })}

        {buckets.length > 1 && (
          <path d={linePath} fill="none" stroke="var(--brand-cyan)" strokeWidth="2" />
        )}

        {buckets.map((b, i) =>
          i % labelEvery === 0 ? (
            <text
              key={`label-${b.start}`}
              x={x(i)}
              y={height - 8}
              textAnchor="middle"
              fontSize="10"
              fill="var(--text-subtle)"
            >
              {shortLabel(b.start)}
            </text>
          ) : null
        )}
      </svg>

      <div className="chart-legend">
        <span>
          <span className="legend-swatch" style={{ background: "var(--brand-indigo)" }} />
          Energy per bucket (kWh)
        </span>
        <span>
          <span className="legend-swatch" style={{ background: "var(--brand-cyan)" }} />
          Average active power (W)
        </span>
      </div>
    </>
  );
}

function shortLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit" });
}
